import { useMutation, useQueryClient } from '@tanstack/react-query';

import BackendService from '../services/backendService';
import { PromptInputData } from '../services/backendService/types';
import {
  GeneratorCoarseLicenseLevel,
  Template,
} from '../services/TemplateService/Template.type';
import { useMetrics } from './useMetrics';
import { useRecentGeneratorsStorage } from './useRecentGenerators';
import useSendSlackMessage from './useSubmitFeedback';

const useConsumeGeneratorAsNewPresentation = (tenantId: string, isTestPrep?: boolean) => {
  const metrics = useMetrics();
  const client = useQueryClient();
  const { saveConsumeGenerator } = useRecentGeneratorsStorage();
  const { submit } = useSendSlackMessage();
  const { mutate: consumeGenerator, isLoading, data, isError, error } = useMutation(
    async ({
      generatorId,
      generatorName,
      generatorPlan,
      userInputFormData,
      isRetry,
    }: {
      generatorId: string;
      generatorName?: string;
      generatorPlan?: GeneratorCoarseLicenseLevel | null | undefined;
      isRetry?: boolean;
      userInputFormData: PromptInputData[];
      onSuccessFunction?: (template: Template) => void;
    }) => {
      metrics.logEvent('Generator.Consume', {
        generatorId,
        generatorName,
        generatorPlan,
        userInputFormData,
        isRetry,
        isPresentation: true,
      });
      const res = await BackendService.instance.ai.consumeGeneratorAsNewPresentation(
        {
          generatorId,
          userInputFormData,
        },
        tenantId,
      );
      return res;
    },
    {
      onSuccess: (data, { onSuccessFunction, generatorId }) => {
        client.invalidateQueries(['hasReachedLimit']);
        saveConsumeGenerator(generatorId);
        if (onSuccessFunction && data) {
          onSuccessFunction(data);
        }
        return true;
      },
      cacheTime: 1000 * 60 * 5,

      onError: (
        error: any,
        { generatorId, generatorName }: { generatorId: string; generatorName?: string },
      ) => {
        submit({
          message: `\n*Error consuming ${
            isTestPrep ? 'test prep lesson' : 'customizable lesson'
          }:* ${
            error.customMessage
          }\n*Generator ID: ${generatorId}\n*Generator Name: ${generatorName}\n*Error message:* ${
            error.message
          }\n*Error status:* ${error.response?.status}\nError code: ${error.code}`,
          accepted: false,
        });
      },
    },
  );

  return { consumeGenerator, isLoading, isError, data, error };
};

export default useConsumeGeneratorAsNewPresentation;
